import { getBackendUrl } from "@/lib/backend";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, Users, Building2, Coins, ArrowRight } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import CheckoutDialog from "@/components/CheckoutDialog";
import CustomTopUpDialog from "@/components/CustomTopUpDialog";

const API = `${getBackendUrl()}/api`;

// Pack feature lists (copy lives in the UI; pricing comes from the backend)
const PACK_FEATURES = {
  starter: [
    "100 renders or refines",
    "All Indian style presets",
    "Multi-view & lasso refine",
    "Portfolio & lookbook export",
    "Credits never expire",
  ],
  studio: [
    "500 renders or refines",
    "Everything in Starter",
    "Bill-of-Materials estimator",
    "CAD starter kit export (.3dm + STL)",
    "₹14 / credit · best for daily use",
  ],
  atelier: [
    "1,300 renders or refines",
    "Everything in Studio",
    "₹12 / credit · best individual rate",
    "Bulk client portfolios",
    "Priority support",
  ],
};

const PACK_CTA = {
  starter: "Buy Starter",
  studio: "Buy Studio",
  atelier: "Buy Atelier",
};

// Workspace plan feature lists. Order/values stay in sync with backend
// PLAN_PRESETS (`/api/workspaces/plans`).
const PLAN_FEATURES = {
  team: [
    "5 designer seats included",
    "400 credits / month (₹12/cr)",
    "Lead-controlled wallet + roster",
    "Catalog folders, ZIP, multi-format",
    "Add extra seats at ₹599/mo each",
  ],
  enterprise: [
    "10 designer seats included",
    "1,300 credits / month (₹12/cr)",
    "Workspace ROI analytics",
    "Watermarks, brand templates, batch composer",
    "Add extra seats at ₹799/mo each",
  ],
};

function formatINR(amount) {
  return `₹${Number(amount).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function Pricing() {
  const [packs, setPacks] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(true);
  const [checkoutPack, setCheckoutPack] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/billing/packs`).then((r) => r.data?.packs || []).catch(() => []),
      axios.get(`${API}/workspaces/plans`).then((r) => (r.data?.plans || []).filter((p) => p.billable)).catch(() => []),
    ])
      .then(([p, w]) => { setPacks(p); setPlans(w); })
      .finally(() => setLoadingPacks(false));
  }, []);

  const onBuy = (pack) => {
    if (!pack?.amount) return;
    setCheckoutPack(pack);
  };

  return (
    <section id="pricing" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-5">Credit packs</p>
          <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] text-white">
            Pay only for what you <span className="italic gold-gradient-text">render</span>.
          </h2>
          <p className="mt-4 text-sm md:text-base text-white/55 max-w-xl">
            Credits never expire. Use them across renders, refines, multi-view sheets and CAD exports.
            Students &amp; institutions: ask us for an Edu code.
          </p>
        </motion.div>

        {/* Individual credit packs */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="pricing-packs-grid">
          {(packs.length ? packs : [{ id: "starter" }, { id: "studio", highlight: true }, { id: "atelier" }]).map((p, i) => {
            const features = PACK_FEATURES[p.id] || [];
            const cta = PACK_CTA[p.id] || "Buy pack";
            const featured = !!p.highlight;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                data-testid={`pricing-card-${p.id}`}
                className={`relative rounded-3xl p-8 md:p-9 transition-all duration-500 ${
                  featured
                    ? "bg-gradient-to-b from-[#D4AF37]/[0.08] to-transparent border border-[#D4AF37]/45 shadow-[0_30px_80px_-30px_rgba(212,175,55,0.35)]"
                    : "glass-card"
                }`}
              >
                {featured && (
                  <span className="absolute top-5 right-5 text-[10px] uppercase tracking-[0.22em] text-black bg-[#D4AF37] px-2.5 py-1 rounded-full font-medium">
                    Most chosen
                  </span>
                )}
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
                  <Sparkles className="w-3 h-3" /> {p.credits || "—"} credits
                </div>
                <div className="font-display text-2xl text-white tracking-tight mt-1">{p.name || "—"}</div>
                <p className="mt-2 text-sm text-white/55">{p.subtitle || ""}</p>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-display text-5xl text-white">
                    {p.amount ? formatINR(p.amount) : "—"}
                  </span>
                  <span className="text-sm text-white/45">one-time</span>
                </div>
                <ul className="mt-7 space-y-3">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-white/70">
                      <Check
                        className={`w-4 h-4 mt-0.5 flex-shrink-0 ${featured ? "text-[#D4AF37]" : "text-white/60"}`}
                        strokeWidth={2}
                      />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => onBuy(p)}
                  disabled={loadingPacks || !p.amount}
                  data-testid={`pricing-cta-${p.id}`}
                  className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full text-sm font-medium py-3 transition-all duration-300 disabled:opacity-60 disabled:cursor-wait ${
                    featured
                      ? "bg-[#D4AF37] text-black hover:bg-[#F3CE5A]"
                      : "border border-white/15 text-white hover:bg-white/5"
                  }`}
                >
                  {cta} <span>→</span>
                </button>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-8 text-center text-[11px] text-white/45">
          Secure checkout by Razorpay · UPI / Cards / NetBanking · Promo codes apply at checkout.
        </p>

        {/* Team & Enterprise workspace plans */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="mt-24"
        >
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3">For teams &amp; ateliers</p>
          <h3 className="font-display font-light text-3xl md:text-4xl tracking-[-0.02em] text-white max-w-2xl">
            Bring your <span className="italic gold-gradient-text">whole studio</span> on Facetra.
          </h3>
          <p className="mt-3 text-sm md:text-base text-white/55 max-w-2xl">
            Monthly workspace plans bundle credits with seats so leads can push wallets to
            designers, track ROI, and keep every shoot organised in shared folders.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="pricing-plans-grid">
            {(plans.length ? plans : [{ key: "team" }, { key: "enterprise" }]).map((plan, i) => {
              const features = PLAN_FEATURES[plan.key] || [];
              const isEnterprise = plan.key === "enterprise";
              const Icon = isEnterprise ? Building2 : Users;
              return (
                <motion.div
                  key={plan.key}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  data-testid={`pricing-plan-${plan.key}`}
                  className={`relative rounded-3xl p-8 md:p-9 transition-all duration-500 ${
                    isEnterprise
                      ? "bg-gradient-to-br from-[#D4AF37]/[0.08] via-[#1A1306]/40 to-transparent border border-[#D4AF37]/40 shadow-[0_30px_80px_-30px_rgba(212,175,55,0.3)]"
                      : "glass-card"
                  }`}
                >
                  <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
                    <Icon className="w-3 h-3" /> {plan.name || (isEnterprise ? "Enterprise" : "Team")}
                  </div>
                  <div className="font-display text-2xl text-white tracking-tight mt-1">
                    {isEnterprise ? "Atelier-grade enterprise" : "Studio team"}
                  </div>
                  <p className="mt-2 text-sm text-white/55">
                    {isEnterprise
                      ? "10 seats · 1,300 credits / mo · full analytics suite"
                      : "5 seats · 400 credits / mo · shared catalog folders"}
                  </p>
                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="font-display text-5xl text-white">
                      {plan.base_price_inr ? formatINR(plan.base_price_inr) : "—"}
                    </span>
                    <span className="text-sm text-white/45">/ month</span>
                  </div>
                  <ul className="mt-7 space-y-3">
                    {features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-white/70">
                        <Check
                          className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isEnterprise ? "text-[#D4AF37]" : "text-white/60"}`}
                          strokeWidth={2}
                        />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => navigate("/signin?next=/enterprise/team")}
                    data-testid={`pricing-plan-cta-${plan.key}`}
                    className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full text-sm font-medium py-3 transition-all duration-300 ${
                      isEnterprise
                        ? "bg-[#D4AF37] text-black hover:bg-[#F3CE5A]"
                        : "border border-white/15 text-white hover:bg-white/5"
                    }`}
                  >
                    {isEnterprise ? "Talk to founder" : "Start a Team"} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Custom top-up CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="mt-14 rounded-3xl border border-white/10 bg-[#0E0E0E] p-7 md:p-9 flex flex-col md:flex-row items-start md:items-center justify-between gap-5"
          data-testid="pricing-custom-topup"
        >
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
              <Coins className="w-3 h-3" /> Custom top-up
            </div>
            <h4 className="font-display text-xl md:text-2xl text-white mt-1.5">
              Buy <span className="italic gold-gradient-text">any number</span> of credits at ₹12 each.
            </h4>
            <p className="mt-2 text-sm text-white/55">
              Need ₹24,000 → 2,000 credits, or a one-off bulk loadout for a campaign?
              Top up from ₹5,000 in any increment. Credits land instantly and never expire.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCustomOpen(true)}
            data-testid="pricing-custom-topup-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/8 text-[#D4AF37] text-sm font-medium px-5 py-3 hover:bg-[#D4AF37]/15 hover:border-[#D4AF37]/65 transition shrink-0"
          >
            Start a custom top-up <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </motion.div>
      </div>

      <CheckoutDialog
        open={!!checkoutPack}
        onOpenChange={(v) => !v && setCheckoutPack(null)}
        pack={checkoutPack}
        onPaid={() => { /* wallet refresh happens on next /app load */ }}
      />
      <CustomTopUpDialog
        open={customOpen}
        onOpenChange={setCustomOpen}
        onPaid={() => { /* wallet refresh on next /app load */ }}
      />
    </section>
  );
}
